// The platform's branding — its name and its icon — for the console's
// chrome: the browser tab, the navbar and the login page.
//
// The values come from an unauthenticated endpoint on purpose: the login
// page has to show the right name and icon before anyone has a token.
//
// The last answer is kept in localStorage and used from the first frame,
// then refreshed from the server. Without that every page load showed a
// default first and swapped in the configured name and logo a moment
// later. On a browser that has never loaded the console, nothing shows
// until the answer arrives — briefly blank rather than briefly wrong.
//
// The tab title is not set here: App.vue sets it per page, as
// "<page> - <system name>", from systemName below.
import { computed, ref } from 'vue'

/** A page's notice (系统配置 → 登录与注册页面): its text, cleaned rich text, in each language. */
export interface AuthNotice {
  content: string
  content_en: string
}
/** A footer link: an address, or one of the agreements, opened as the agreement notes open them. */
export interface AuthFooterLink {
  kind: 'url' | 'terms' | 'privacy'
  text: string
  text_en: string
  url?: string
}
/** The footer under both pages; {year} and {name} in the copyright are this year and 系统名称. */
export interface AuthFooter {
  links: AuthFooterLink[]
  copyright: string
  copyright_en: string
}

export interface Branding {
  system_name: string
  // Still returned by the server, no longer used: the tab title is the
  // page's own name plus the system name.
  console_title: string
  admin_title: string
  challenge_brand: string
  has_favicon: boolean
  /** Changes exactly when the icon does; "" when there is none. */
  favicon_version?: string
  /** The uploaded logo (系统配置), the mark beside the name; the favicon stands in without one. */
  has_logo?: boolean
  logo_version?: string
  /** Each uploaded logo's version: logo, logo_dark, logo_mini, logo_mini_dark. */
  logos?: Record<string, string>
  /** The login pages' own backgrounds' versions: auth_bg, auth_bg_dark (登录与注册页面). */
  backgrounds?: Record<string, string>
  /** 系统名称 beside the logo (off for a logo that carries the name). */
  show_name?: boolean
  allow_registration: boolean
  /** 短信服务 can text codes: 找回密码 and proving phones are offered. */
  sms?: boolean
  /** …and to numbers outside mainland China too: phone fields offer other countries' codes. */
  sms_intl?: boolean
  /** The platform's theme colour, an Arco palette's name or #RRGGBB (系统配置). */
  skin?: string
  /** How the VIP badge looks (会员等级): standard, facet, holo, capsule or tiered. */
  level_badge?: string
  /** The layout's defaults (系统配置): the top bar's height, the side menu's width. */
  navbar_height?: number
  menu_width?: number
  /** The official site the login page links back to (注册与登录). */
  website_url?: string
  /** Registering proves the email, and a phone, with a code. */
  register_email?: boolean
  register_phone?: boolean
  /** 验证码登录 by a texted or a mailed code. */
  login_sms?: boolean
  login_email?: boolean
  /** The agreements, when there is one to show: its text (fetched apart) or its link. */
  terms?: { mode: 'content' | 'link'; url?: string }
  privacy?: { mode: 'content' | 'link'; url?: string }
  /** 登录与注册页面: each page's notice while it is on, and the footer while it shows. */
  login_notice?: AuthNotice
  register_notice?: AuthNotice
  footer?: AuthFooter
}

const CACHE_KEY = 'cdnx_branding'
const FALLBACK_NAME = 'cdnX'

function cached(): Branding | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as Branding) : null
  } catch {
    return null
  }
}

const current = ref<Branding | null>(cached())
// The request has answered or failed: only then is "no name" a reason to
// fall back to the default rather than to show nothing yet.
const settled = ref(false)

/** 系统名称, as set under 系统设置. Empty until it is known. */
export const systemName = computed(
  () => current.value?.system_name?.trim() || (settled.value ? FALLBACK_NAME : ''),
)

/**
 * The uploaded favicon, or null when there is none: the browser tab's icon.
 * Versioned by the icon's hash, so the browser keeps it between loads and
 * it is there on the first frame; a new upload is a new URL.
 */
export const faviconUrl = computed(() => {
  const b = current.value
  if (!b?.has_favicon) return null
  return `/api/v1/public/favicon.ico?v=${encodeURIComponent(b.favicon_version || 'x')}`
})

const slotUrl = (slot: string, v?: string) => (v ? `/api/v1/public/logo/${slot}?v=${encodeURIComponent(v)}` : null)

/**
 * The logos as each place needs them — the full one and the collapsed
 * menu's, for a light background and a dark one — versioned as the favicon.
 * Each falls back to its other theme's, then to the favicon (a collapsed one
 * past that to the full one); null with none — no mark shows then.
 */
export const logos = computed(() => {
  const b = current.value
  const v: Record<string, string> = { ...(b?.logos ?? {}) }
  // An answer from before there were four.
  if (!v.logo && b?.has_logo && b.logo_version) v.logo = b.logo_version
  const u = (slot: string) => slotUrl(slot, v[slot])
  const fav = faviconUrl.value
  const light = u('logo') ?? u('logo_dark') ?? fav
  const dark = u('logo_dark') ?? u('logo') ?? fav
  return {
    light,
    dark,
    miniLight: u('logo_mini') ?? u('logo_mini_dark') ?? fav ?? light,
    miniDark: u('logo_mini_dark') ?? u('logo_mini') ?? fav ?? dark,
    // A logo of the platform's own, not the favicon standing in.
    own: !!(v.logo || v.logo_dark),
  }
})

/**
 * The login pages' own backgrounds, for the light theme and the dark, each
 * standing in for the other; null with neither — the pages draw their
 * illustration then.
 */
export const authBackgrounds = computed(() => {
  const v = current.value?.backgrounds ?? {}
  const u = (slot: string) => (v[slot] ? `/api/v1/public/auth-bg/${slot}?v=${encodeURIComponent(v[slot])}` : null)
  const light = u('auth_bg') ?? u('auth_bg_dark')
  const dark = u('auth_bg_dark') ?? u('auth_bg')
  return light && dark ? { light, dark } : null
})

/** The full logo on a light background (or what stands in for it). */
export const logoUrl = computed(() => logos.value.light)

/** 系统名称 beside the logo: unless 系统配置 switched it off, and then only
    while there is a logo of the platform's own to show instead. */
export const nameShown = computed(() => current.value?.show_name !== false || !logos.value.own)

/** The theme colour, from the first frame (the cached answer). */
export const skin = computed(() => current.value?.skin || 'arcoblue')
/** The layout's defaults, for whoever has not chosen their own. */
export const layoutDefaults = computed(() => ({
  navbarHeight: current.value?.navbar_height || 60,
  menuWidth: current.value?.menu_width || 210,
}))

export function branding(): Branding | null {
  return current.value
}

export async function applyBranding(): Promise<Branding | null> {
  try {
    // Plain fetch rather than the api client: that client attaches the
    // auth header and redirects to /login on a 401, neither of which
    // applies to an endpoint that is meant to work logged out.
    const res = await fetch('/api/v1/public/branding', { cache: 'no-store' })
    if (!res.ok) return current.value
    current.value = (await res.json()) as Branding
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(current.value))
    } catch {
      /* private mode: the next load just starts blank again */
    }
    setFavicon(faviconUrl.value)
    return current.value
  } catch {
    // Branding is cosmetic. A console that refused to start because it
    // could not fetch a name would be a much worse failure than a
    // console showing its default one.
    return current.value
  } finally {
    settled.value = true
  }
}

function setFavicon(href: string | null) {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
  if (!href) {
    link?.remove()
    return
  }
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.type = 'image/x-icon'
  if (link.getAttribute('href') !== href) link.href = href
}

// The tab icon from the cached answer, before the request goes out.
setFavicon(faviconUrl.value)
